import { newMindmapNodeElement } from "../../packages/excalidraw/element/newElement";
import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import type {
  ExcalidrawElement,
  IntegrationMindmapNodeData,
} from "../../packages/excalidraw/element/types";
import { INTEGRATION_STYLE } from "../../packages/excalidraw/constants";
import { getCommonBounds } from "../../packages/excalidraw/element/bounds";

import { MINDMAP_NEUTRAL_COLOR } from "../../packages/mindmap-core/src/mindmapColors";
import {
  mindmapNodeHeight,
  mindmapNodeWidth,
} from "../../packages/mindmap-core/src/mindmapLayout";
import type { MindmapTree } from "../../packages/mindmap-core/src/mindmapTree";
import type { Bounds } from "../../packages/mindmap-core/src/mindmapPlacement";
import {
  computeMapPlacement,
  viewportSceneCenter,
} from "../../packages/mindmap-core/src/mindmapPlacement";

/**
 * "Add as card" from the Notion palette (Fix 5c). Drops a STANDALONE page pill
 * (nodeKind "page", no `customData.mindmap`, so no mapId and no expand chip)
 * that opens the Notion side peek on click, matching the look of generated map
 * nodes.
 *
 * {@link standaloneNotionCardSpec} is the same pill without the placement, for
 * the shape-replacement flows that position and size it themselves (C9#4).
 * Every path that used to mint a legacy `notion` element now goes through one of
 * the two; the `notion` type survives only to render scenes that already have
 * one.
 */

export interface StandaloneNotionCardInput {
  pageId: string;
  title: string;
  icon?: { type: string; [key: string]: string } | null;
  /**
   * True when `pageId` is a DATABASE (data_source) id rather than a page id.
   * The card is then emitted as nodeKind "database" so the renderer's page-only
   * side peek skips it — opening a data_source id as a page always errors.
   */
  isDatabase?: boolean;
}

/**
 * Bounding box of existing content to avoid, or null when there is none.
 * Tombstones are excluded so the card never dodges invisible content.
 */
const occupiedBounds = (
  elements: readonly ExcalidrawElement[],
): Bounds | null => {
  const real = elements.filter(
    (el) =>
      !el.isDeleted &&
      Number.isFinite(el.x) &&
      Number.isFinite(el.y) &&
      Number.isFinite(el.width) &&
      Number.isFinite(el.height),
  );
  return real.length ? (getCommonBounds(real) as Bounds) : null;
};

/**
 * Build a standalone page pill for `input`, place it at the viewport center
 * (nudged clear of existing content), and insert it in a single committed
 * `updateScene`, then zoom to it. Carries NO mindmap identity, so refresh and
 * expand never touch it and it shows no chip — it is just a live page card.
 */
export const standaloneNotionCardSpec = (
  input: StandaloneNotionCardInput,
): IntegrationMindmapNodeData => {
  // A database card is nodeKind "database", not "page": the side peek is gated
  // on "page", so this is what stops a data_source id being opened as a page.
  const nodeKind = input.isDatabase ? "database" : "page";
  const treeLike: MindmapTree = {
    blockId: input.pageId,
    text: input.title,
    kind: nodeKind,
    pageId: input.pageId,
    hasChildren: false,
    children: [],
  };

  return {
    label: input.title,
    nodeKind,
    pageId: input.pageId,
    icon: input.icon ?? null,
    branchColor: MINDMAP_NEUTRAL_COLOR,
    width: mindmapNodeWidth(treeLike),
    height: mindmapNodeHeight(treeLike),
  };
};

export const addStandaloneNotionCard = (
  api: ExcalidrawImperativeAPI,
  input: StandaloneNotionCardInput,
): void => {
  const spec = standaloneNotionCardSpec(input);

  const card = newMindmapNodeElement({
    x: 0,
    y: 0,
    ...INTEGRATION_STYLE,
    strokeColor: spec.branchColor,
    ...spec,
    // Standalone: no children, no chip, no map identity.
    expandable: false,
    expanded: false,
  }) as ExcalidrawElement;

  // Including tombstones: `updateScene` replaces the scene wholesale, so
  // committing the live-only array would purge every deletion on the canvas.
  const existing = api.getSceneElementsIncludingDeleted();
  const { dx, dy } = computeMapPlacement(
    getCommonBounds([card]) as Bounds,
    viewportSceneCenter(api.getAppState()),
    occupiedBounds(existing),
  );
  const placed = { ...card, x: card.x + dx, y: card.y + dy };

  api.updateScene({
    elements: [...existing, placed],
    commitToHistory: true,
  });
  api.scrollToContent([placed], { fitToContent: true });
};
